import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as client from "../../../client";
import { ExactMoney, formatExactMoneyDollars } from "../Money";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { downloadBlob, useRandomId } from "../../../util";
import styles from "./LossRunCard.module.css";
import { TabView, TabPanel } from "primereact/tabview";
import { CATCloudIcon } from "./CloudIcon";
import { SafeReportResponse } from "../Conversion";
import { Button } from "primereact/button";
import { downloadLossRun } from "../../../api/routes";
import { InputText } from "primereact/inputtext";
import { Fragment, useContext, useMemo, useRef, useState } from "react";
import { SortOrder } from "primereact/api";
import {
  claimsFromLossRunInfo,
  Loss,
  LossRunByYear,
  LossRunWindowStats,
  lossRunInfoToLossRunYears,
  lossRunStatsForWindow,
  maxWindowYearsForLossRuns,
  parsePolicyPeriod,
  signatureForSourceDetails,
} from "./util";
import {
  HIGHLIGHT_USER_OVERRIDE_DARK_MODE,
  HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE,
  HIGHLIGHT_WARNING_DARK_MODE,
  HIGHLIGHT_WARNING_LIGHT_MODE,
} from "../dashboard/util";
import { ThemeContext } from "../../../themes/Theme";
import {
  flagTextForFlags,
  flagsForLoss,
  flagsForLossYear,
  LossFlag,
} from "./flags";
import { useUpdateReport } from "../data";
import { CommentContainer } from "../../../components/comments/Comment";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { cloneDeep, groupBy, isEqual } from "lodash";

// Sentinel SourceDetails identifying loss run years entered manually via the
// "Add Years" modal. Mirrors MANUAL_LOSS_RUN_SOURCE_DETAILS in the backend so
// the source signatures match across the API boundary.
const MANUAL_LOSS_RUN_SOURCE_DETAILS: client.SourceDetails = {
  filename: "Manual entry",
  sheet_name: null,
  data_type: "summary",
  calc_mode: null,
  carrier: null,
};

// XXX very dangerous
export const TerribleMoney = ({
  dollars,
  ifUndefined,
}: {
  dollars?: number | null;
  ifUndefined?: string;
}) => {
  return (
    <>
      {dollars !== undefined && dollars !== null ? (
        <ExactMoney cents={Math.round(dollars * 100)} />
      ) : ifUndefined !== undefined ? (
        ifUndefined
      ) : null}
    </>
  );
};

const USER_EDIT_FLAG_IDS = new Set<LossFlag["flagId"]>([
  "user_added_year",
  "user_added_claim",
  "user_edited",
]);

export const FlagWrapper = ({
  flags,
  children,
}: {
  flags?: LossFlag[];
  children: React.ReactNode;
}) => {
  const highlightWarning = flagTextForFlags(flags ?? []);
  const warningId = useRandomId();

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const allUserEdits =
    !!flags &&
    flags.length > 0 &&
    flags.every((f) => USER_EDIT_FLAG_IDS.has(f.flagId));
  const backgroundColor = allUserEdits
    ? isLightMode
      ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
      : HIGHLIGHT_USER_OVERRIDE_DARK_MODE
    : isLightMode
      ? HIGHLIGHT_WARNING_LIGHT_MODE
      : HIGHLIGHT_WARNING_DARK_MODE;
  return (
    <>
      {highlightWarning ? (
        <Tooltip target={"." + warningId} className={styles.tooltip}>
          {highlightWarning}
        </Tooltip>
      ) : null}
      <div className={styles.horizontalContainer}>
        <div style={{ display: "flex" }}>
          <div
            className={warningId}
            style={highlightWarning ? { padding: "2px", backgroundColor } : {}}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

type LossRunCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

const formatStatCell = (avg: number | null, total: number | null) => {
  if (avg === null) return "N/A";
  return (
    <>
      {`${formatExactMoneyDollars(avg, { withRounding: true, withDollarSign: true })}/y`}
      {total !== null && (
        <span className={styles.summaryStatSecondary}>
          ·{" "}
          {formatExactMoneyDollars(total, {
            withRounding: true,
            withDollarSign: true,
          })}{" "}
          total
        </span>
      )}
    </>
  );
};

const SummaryStatTable = ({
  rows,
}: {
  rows: { label: string; stats: LossRunWindowStats }[];
}) => (
  <div className={styles.summaryStatTableWrapper}>
    <table className={styles.summaryStatTable}>
      <thead>
        <tr>
          <th />
          <th>Ground Up</th>
          <th>Net Incurred</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ label, stats }) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>
              {formatStatCell(stats.avgGroundUpLoss, stats.totalGroundUpLoss)}
            </td>
            <td>
              {formatStatCell(stats.avgNetIncurred, stats.totalNetIncurred)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const download = async (
  lossRunYears: client.UserGeneratedLossRunByYear[],
  companyName: string
) => {
  // Download the zip file as a blob
  const blob = await downloadLossRun(lossRunYears);
  const filename = `${companyName}_Loss_Run_.xlsx`;
  downloadBlob(filename, blob);
};

const SourcesModal = ({
  initialLossRunInfo,
  reportId,
  visible,
  onClose,
}: {
  initialLossRunInfo: client.LossRunInfo;
  reportId: string;
  visible: boolean;
  onClose: () => void;
}) => {
  const updateReport = useUpdateReport(reportId);
  const isLoading = updateReport.isPending;
  const [lossRunInfo, setLossRunInfo] = useState(cloneDeep(initialLossRunInfo));
  const allSourceDetails = Object.values(lossRunInfo.enabled_by_source).map(
    (info) => info.source_details
  );
  const groupedSourceDetails = groupBy(allSourceDetails, "filename");

  const onChangeCheckmark = async (signature: string, isChecked: boolean) => {
    setLossRunInfo((oldInfo) => {
      const newInfo = cloneDeep(oldInfo);
      const details = newInfo.enabled_by_source[signature];
      if (details) {
        details.enabled = isChecked;
      }
      return newInfo;
    });
  };

  const onSave = async () => {
    await updateReport.mutateAsync({ lossRunInfo: lossRunInfo });
  };

  const hasChanges = !isEqual(initialLossRunInfo, lossRunInfo);

  return (
    <Dialog
      header={"Sources"}
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => onClose()}
    >
      <div
        className={styles.verticalContainer}
        style={{ justifyContent: "space-between", height: "100%" }}
      >
        <div className={styles.verticalContainer}>
          {Object.entries(groupedSourceDetails).map(
            ([filename, sourceDetails]) => {
              const sourceIdentifiers: { [key: string]: string } = {};

              for (const details of sourceDetails) {
                sourceIdentifiers[signatureForSourceDetails(details)] = `${
                  details.sheet_name ? `${details.sheet_name} ` : ""
                }${details.carrier ? `${details.carrier} ` : ""}${
                  details.calc_mode
                    ? `${details.calc_mode === "as_if" ? "as if" : "historical"} `
                    : ""
                }${
                  details.data_type === "claim" ? "claim data" : "summary data"
                } `;
              }

              return (
                <div className={styles.verticalContainer} key={filename}>
                  <span
                    style={{
                      fontWeight: "bold",
                      marginTop: "8px",
                    }}
                  >
                    {filename}
                  </span>
                  {Object.entries(sourceIdentifiers).map(
                    ([signature, readable]) => {
                      const isEnabled =
                        !!lossRunInfo.enabled_by_source[signature]?.enabled;
                      return (
                        <div
                          className={styles.horizontalContainer}
                          key={signature}
                          style={{ marginLeft: "16px" }}
                        >
                          <Checkbox
                            checked={isEnabled}
                            onChange={(e) =>
                              onChangeCheckmark(signature, e.checked ?? false)
                            }
                            disabled={isLoading}
                          />
                          <span>{readable}</span>
                        </div>
                      );
                    }
                  )}
                </div>
              );
            }
          )}
        </div>
        <div className={styles.horizontalContainer}>
          <Button
            label="Cancel"
            disabled={isLoading || !hasChanges}
            onClick={() => setLossRunInfo(initialLossRunInfo)}
            severity="secondary"
          />
          <Button
            label="Save"
            loading={isLoading}
            disabled={!hasChanges}
            onClick={() => onSave()}
          />
        </div>
      </div>
    </Dialog>
  );
};

type EditableLossYearRow = {
  startYear: string;
  endYear: string;
  groundUpLoss: string;
  netIncurred: string;
  // Hidden from the form for now but preserved if editing an existing row.
  description: string;
  openClaims: number;
  closedClaims: number;
};

const blankRowForStartYear = (startYear: number): EditableLossYearRow => ({
  startYear: String(startYear),
  endYear: String(startYear + 1),
  groundUpLoss: "0",
  netIncurred: "0",
  description: "",
  openClaims: 0,
  closedClaims: 0,
});

// Default rows shown when the user has no existing manual entries. For 2026
// this yields start years 2025..2021 (policy periods 2025-2026 down to
// 2021-2022), the most recent five policy years including the current
// in-progress one.
const defaultRowsForCurrentYear = (): EditableLossYearRow[] => {
  const currentYear = new Date().getFullYear();
  const rows: EditableLossYearRow[] = [];
  for (let i = 0; i < 5; i++) {
    rows.push(blankRowForStartYear(currentYear - 1 - i));
  }
  return rows;
};

const existingManualRowsFromLossRunInfo = (
  lossRunInfo: client.LossRunInfo
): EditableLossYearRow[] => {
  const rows: EditableLossYearRow[] = [];
  for (const summaries of Object.values(lossRunInfo.loss_runs_by_year)) {
    for (const summary of summaries) {
      if (summary.source !== "user_generated") continue;
      const parsed = parsePolicyPeriod(summary.policy_year);
      rows.push({
        startYear: parsed ? String(parsed[0]) : "",
        endYear: parsed ? String(parsed[1]) : "",
        groundUpLoss: String(summary.ground_up_loss ?? 0),
        netIncurred: String(summary.net_incurred ?? 0),
        description: summary.description_of_losses,
        openClaims: summary.open_claims,
        closedClaims: summary.closed_claims,
      });
    }
  }
  rows.sort(
    (a, b) =>
      (parseInt(b.startYear, 10) || 0) - (parseInt(a.startYear, 10) || 0)
  );
  return rows;
};

const isFourDigitYear = (value: string): boolean => /^\d{4}$/.test(value);

const rowIsValid = (row: EditableLossYearRow): boolean => {
  if (!isFourDigitYear(row.startYear) || !isFourDigitYear(row.endYear)) {
    return false;
  }
  return parseInt(row.endYear, 10) > parseInt(row.startYear, 10);
};

const AddLossRunYearsModal = ({
  initialLossRunInfo,
  reportId,
  onClose,
}: {
  initialLossRunInfo: client.LossRunInfo;
  reportId: string;
  onClose: () => void;
}) => {
  const updateReport = useUpdateReport(reportId);
  const isLoading = updateReport.isPending;

  const initialManualRows = useMemo(
    () => existingManualRowsFromLossRunInfo(initialLossRunInfo),
    [initialLossRunInfo]
  );
  const [rows, setRows] = useState<EditableLossYearRow[]>(() =>
    initialManualRows.length > 0
      ? initialManualRows
      : defaultRowsForCurrentYear()
  );

  const updateRow = (index: number, patch: Partial<EditableLossYearRow>) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        // Auto-bump end year when start year changes and end was the previous default.
        if (
          patch.startYear !== undefined &&
          isFourDigitYear(patch.startYear) &&
          (!isFourDigitYear(row.endYear) ||
            parseInt(row.endYear, 10) === parseInt(row.startYear, 10) + 1)
        ) {
          next.endYear = String(parseInt(patch.startYear, 10) + 1);
        }
        return next;
      })
    );
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setRows((prev) => {
      const earliestStart = prev
        .map((row) => parseInt(row.startYear, 10))
        .filter((n) => Number.isFinite(n))
        .reduce((min, n) => Math.min(min, n), new Date().getFullYear() - 1);
      return [...prev, blankRowForStartYear(earliestStart - 1)];
    });
  };

  const policyYears = rows.map((row) =>
    rowIsValid(row) ? `${row.startYear}-${row.endYear}` : null
  );
  const duplicateYears = new Set<string>();
  const seen = new Set<string>();
  for (const py of policyYears) {
    if (py === null) continue;
    if (seen.has(py)) duplicateYears.add(py);
    seen.add(py);
  }
  const allRowsValid = rows.every(rowIsValid);
  const hasChanges = !isEqual(rows, initialManualRows);
  const manualSig = signatureForSourceDetails(MANUAL_LOSS_RUN_SOURCE_DETAILS);
  // Saved manual years can exist while the "Manual entry" source is disabled
  // in the Sources modal, which hides them from the tables. Saving re-enables
  // the source, so allow a save in that state even with no row edits.
  const manualSourceDisabled =
    initialManualRows.length > 0 &&
    !initialLossRunInfo.enabled_by_source[manualSig]?.enabled;
  const canSave =
    (hasChanges || manualSourceDisabled) &&
    allRowsValid &&
    duplicateYears.size === 0;

  const onSave = async () => {
    const newLossRunsByYear: client.LossRunInfo["loss_runs_by_year"] = {};
    for (const [year, summaries] of Object.entries(
      initialLossRunInfo.loss_runs_by_year
    )) {
      const filtered = summaries.filter(
        (summary) => summary.source !== "user_generated"
      );
      if (filtered.length > 0) newLossRunsByYear[year] = filtered;
    }

    for (const row of rows) {
      if (!rowIsValid(row)) continue;
      const policyYear = `${row.startYear}-${row.endYear}`;
      const entry: client.UserGeneratedLossRunByYear = {
        policy_year: policyYear,
        description_of_losses: row.description,
        calc_mode: null,
        source_details: MANUAL_LOSS_RUN_SOURCE_DETAILS,
        open_claims: row.openClaims,
        closed_claims: row.closedClaims,
        ground_up_loss: parseFloat(row.groundUpLoss) || 0,
        net_incurred: parseFloat(row.netIncurred) || 0,
        source: "user_generated",
      };
      const existingForYear = newLossRunsByYear[policyYear];
      if (existingForYear) {
        existingForYear.push(entry);
      } else {
        newLossRunsByYear[policyYear] = [entry];
      }
    }

    const newEnabledBySource = { ...initialLossRunInfo.enabled_by_source };
    const hasManualRows = rows.some(rowIsValid);
    if (hasManualRows) {
      // Saving years is an explicit request to show them, so re-enable the
      // manual source even if it was unchecked in the Sources modal.
      newEnabledBySource[manualSig] = {
        source_details: MANUAL_LOSS_RUN_SOURCE_DETAILS,
        enabled: true,
      };
    } else {
      delete newEnabledBySource[manualSig];
    }

    await updateReport.mutateAsync({
      lossRunInfo: {
        loss_runs_by_year: newLossRunsByYear,
        enabled_by_source: newEnabledBySource,
      },
    });
    onClose();
  };

  return (
    <Dialog
      header={"Add Loss Run Years"}
      visible={true}
      style={{ width: "min(720px, calc(100vw - 72px))" }}
      onHide={() => onClose()}
    >
      <div
        className={styles.verticalContainer}
        style={{ justifyContent: "space-between" }}
      >
        <div className={styles.lossYearGrid}>
          <span className={styles.lossYearGridHeader}>Start Year</span>
          <span />
          <span className={styles.lossYearGridHeader}>End Year</span>
          <span className={styles.lossYearGridHeader}>Ground Up Loss</span>
          <span className={styles.lossYearGridHeader}>Net Incurred</span>
          <span />
          {rows.map((row, idx) => {
            const policyYear = `${row.startYear}-${row.endYear}`;
            const invalid = !rowIsValid(row);
            const duplicate = duplicateYears.has(policyYear);
            return (
              <Fragment key={idx}>
                <InputText
                  type="number"
                  value={row.startYear}
                  onChange={(e) =>
                    updateRow(idx, { startYear: e.target.value })
                  }
                  className={
                    (invalid && !isFourDigitYear(row.startYear)) || duplicate
                      ? "p-invalid"
                      : undefined
                  }
                  disabled={isLoading}
                />
                <span style={{ textAlign: "center" }}>—</span>
                <InputText
                  type="number"
                  value={row.endYear}
                  onChange={(e) => updateRow(idx, { endYear: e.target.value })}
                  className={invalid || duplicate ? "p-invalid" : undefined}
                  disabled={isLoading}
                />
                <InputText
                  type="number"
                  value={row.groundUpLoss}
                  onChange={(e) =>
                    updateRow(idx, { groundUpLoss: e.target.value })
                  }
                  disabled={isLoading}
                />
                <InputText
                  type="number"
                  value={row.netIncurred}
                  onChange={(e) =>
                    updateRow(idx, { netIncurred: e.target.value })
                  }
                  disabled={isLoading}
                />
                <Button
                  icon="pi pi-trash"
                  text
                  severity="danger"
                  disabled={isLoading}
                  onClick={() => removeRow(idx)}
                />
              </Fragment>
            );
          })}
        </div>
        <div>
          <Button
            label="Add Year"
            icon="pi pi-plus"
            text
            disabled={isLoading}
            onClick={addRow}
          />
        </div>
        {duplicateYears.size > 0 && (
          <Message
            severity="warn"
            text="Duplicate policy years are not allowed."
          />
        )}
        {rows.length > 0 && !allRowsValid && (
          <Message
            severity="warn"
            text="Each row needs a 4-digit start and end year, with end year greater than start year."
          />
        )}
        <div className={styles.horizontalContainer}>
          <Button
            label="Cancel"
            disabled={isLoading}
            onClick={onClose}
            severity="secondary"
          />
          <Button
            label="Save"
            loading={isLoading}
            disabled={!canSave}
            onClick={onSave}
          />
        </div>
      </div>
    </Dialog>
  );
};

export const LossRunCard: React.FC<LossRunCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [showAddYearsModal, setShowAddYearsModal] = useState(false);
  const lossRunInfo = specialtyPropertyInfo.report_json.loss_run;
  const claims = claimsFromLossRunInfo(lossRunInfo);
  const lossRunYears = lossRunInfoToLossRunYears(lossRunInfo);
  // Any user_generated entry means a year has been added or inline-edited, so
  // the modal now edits existing manual values rather than starting fresh.
  const hasUserGeneratedYears = Object.values(
    lossRunInfo.loss_runs_by_year
  ).some((summaries) =>
    summaries.some((summary) => summary.source === "user_generated")
  );
  const windowStats = lossRunStatsForWindow(lossRunYears, 5);
  const maxWindowYears = maxWindowYearsForLossRuns(lossRunYears);
  const fullWindowStats =
    maxWindowYears > 5
      ? lossRunStatsForWindow(lossRunYears, maxWindowYears)
      : null;
  const [activeIndex, setActiveIndex] = useState(0);

  // Treat each year as a parsed year just to make things easier on the backend
  const lossRunYearsAsUserGenerated: client.UserGeneratedLossRunByYear[] =
    lossRunYears.map((year) => {
      if (year.source === "inferred") {
        return {
          ...year,
          source: "user_generated",
          calc_mode: null,
          source_details: {
            filename: "",
            sheet_name: "",
            calc_mode: null,
            data_type: "summary",
            carrier: null,
          },
        };
      } else if (year.source === "parsed_year") {
        return { ...year, source: "user_generated" };
      } else if (year.source === "computed_from_claims") {
        return { ...year, source: "user_generated" };
      } else {
        return year;
      }
    });

  return (
    <Card
      title={
        <div className={styles.titleRow}>
          <span>Loss Run</span>
          {windowStats.yearsCovered > 0 && (
            <SummaryStatTable
              rows={[
                {
                  label: `${windowStats.windowYears} yr`,
                  stats: windowStats,
                },
                ...(fullWindowStats && fullWindowStats.yearsCovered > 0
                  ? [{ label: "all", stats: fullWindowStats }]
                  : []),
              ]}
            />
          )}
          {windowStats.yearsCovered > 0 &&
            windowStats.yearsCovered < windowStats.windowYears && (
              <Message
                className={styles.summaryMessage}
                severity="error"
                text={`Insufficient Loss History. Only ${windowStats.yearsCovered} year${windowStats.yearsCovered === 1 ? "" : "s"} provided.`}
              />
            )}
        </div>
      }
      style={{ position: "relative" }}
    >
      <SourcesModal
        visible={showSourcesModal}
        reportId={specialtyPropertyInfo.report_id}
        initialLossRunInfo={lossRunInfo}
        onClose={() => setShowSourcesModal(false)}
      />
      {showAddYearsModal && (
        <AddLossRunYearsModal
          reportId={specialtyPropertyInfo.report_id}
          initialLossRunInfo={lossRunInfo}
          onClose={() => setShowAddYearsModal(false)}
        />
      )}
      <div className={styles.buttonContainer}>
        <Button
          label={hasUserGeneratedYears ? "Edit Years" : "Add Years"}
          severity="secondary"
          raised
          text
          icon={hasUserGeneratedYears ? "pi pi-pencil" : "pi pi-plus"}
          onClick={() => setShowAddYearsModal(true)}
        />
        <Button
          label="Sources"
          severity="secondary"
          raised
          text
          icon="pi pi-file-excel"
          onClick={() => setShowSourcesModal(true)}
        />
        <Button
          label="Download"
          severity="secondary"
          raised
          text
          icon="pi pi-file-excel"
          onClick={() =>
            download(
              lossRunYearsAsUserGenerated,
              specialtyPropertyInfo.report_json.company_info.company_name
            )
          }
        />
      </div>
      <TabView
        style={{ marginTop: "-8px" }}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="By Year">
          <YearByYearLossRunTable
            claims={claims}
            lossRunInfo={lossRunInfo}
            reportId={specialtyPropertyInfo.report_id}
            reportName={
              specialtyPropertyInfo.report_json.company_info.company_name
            }
          />
        </TabPanel>
        <TabPanel header="By Claim">
          <ClaimByClaimLossRunTable
            claims={claims}
            lossRunInfo={lossRunInfo}
            reportId={specialtyPropertyInfo.report_id}
            reportName={
              specialtyPropertyInfo.report_json.company_info.company_name
            }
          />
        </TabPanel>
      </TabView>
    </Card>
  );
};

type LossRunTableProps = {
  claims: Loss[];
  reportId: string;
  reportName: string;
  lossRunInfo: client.LossRunInfo;
};

// Numeric loss-year fields that can be edited inline by double-clicking the
// cell. Claim counts are integers; the money fields are floats.
type EditableLossField =
  | "open_claims"
  | "closed_claims"
  | "ground_up_loss"
  | "net_incurred";

const parseEditableField = (
  field: EditableLossField,
  raw: string
): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (field === "open_claims" || field === "closed_claims") {
    const n = Number.parseInt(trimmed, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const n = Number.parseFloat(trimmed);
  return Number.isFinite(n) ? n : null;
};

const YearByYearLossRunTable: React.FC<LossRunTableProps> = ({
  lossRunInfo,
  reportId,
  reportName,
}) => {
  const lossRuns = lossRunInfoToLossRunYears(lossRunInfo);
  const updateReport = useUpdateReport(reportId);

  const [editing, setEditing] = useState<{
    policyYear: string;
    field: EditableLossField;
  } | null>(null);
  const [draft, setDraft] = useState("");
  // The cell whose save is currently in flight, shown with a spinner.
  const [savingCell, setSavingCell] = useState<{
    policyYear: string;
    field: EditableLossField;
  } | null>(null);
  // Set when Escape cancels an edit so the ensuing blur skips the save.
  const cancelledRef = useRef(false);

  // Persist an inline edit by writing the value into a user_generated entry for
  // that policy year. If no manual entry exists yet, seed one from the currently
  // displayed (parsed/computed) values so only the edited field changes. The
  // manual entry takes precedence in mergedLossYearsForPolicyYear, so the edit
  // surfaces as an override without mutating the parsed sources.
  const saveEditedField = async (
    row: LossRunByYear,
    field: EditableLossField,
    value: number
  ) => {
    const newInfo = cloneDeep(lossRunInfo);
    const existing = newInfo.loss_runs_by_year[row.policy_year] ?? [];
    let userEntry = existing.find(
      (entry): entry is client.UserGeneratedLossRunByYear =>
        entry.source === "user_generated"
    );
    if (!userEntry) {
      userEntry = {
        policy_year: row.policy_year,
        description_of_losses: row.description_of_losses,
        calc_mode: null,
        source_details: MANUAL_LOSS_RUN_SOURCE_DETAILS,
        open_claims: row.open_claims,
        closed_claims: row.closed_claims,
        ground_up_loss: row.ground_up_loss,
        net_incurred: row.net_incurred,
        source: "user_generated",
      };
      existing.push(userEntry);
      newInfo.loss_runs_by_year[row.policy_year] = existing;
    }
    userEntry[field] = value;

    const manualSig = signatureForSourceDetails(MANUAL_LOSS_RUN_SOURCE_DETAILS);
    if (!newInfo.enabled_by_source[manualSig]) {
      newInfo.enabled_by_source[manualSig] = {
        source_details: MANUAL_LOSS_RUN_SOURCE_DETAILS,
        enabled: true,
      };
    }

    await updateReport.mutateAsync({ lossRunInfo: newInfo });
  };

  const beginEdit = (row: LossRunByYear, field: EditableLossField) => {
    const current = row[field];
    setDraft(current === null || current === undefined ? "" : String(current));
    setEditing({ policyYear: row.policy_year, field });
  };

  const commitEdit = async (row: LossRunByYear, field: EditableLossField) => {
    setEditing(null);
    const parsed = parseEditableField(field, draft);
    if (parsed === null) return;
    if (parsed === (row[field] ?? null)) return;
    setSavingCell({ policyYear: row.policy_year, field });
    try {
      await saveEditedField(row, field, parsed);
    } finally {
      setSavingCell(null);
    }
  };

  const renderEditableCell = (
    row: LossRunByYear,
    field: EditableLossField,
    display: React.ReactNode
  ) => {
    // Only real policy years can be edited; the "all_years" aggregate and any
    // unparseable rows are read-only.
    const isEditable = parsePolicyPeriod(row.policy_year) !== null;
    const isEditing =
      editing?.policyYear === row.policy_year && editing?.field === field;
    const isSaving =
      savingCell?.policyYear === row.policy_year && savingCell?.field === field;

    if (isSaving) {
      return (
        <div className={styles.savingCell} aria-busy="true">
          {display}
          <i className="pi pi-spin pi-spinner" />
        </div>
      );
    }

    if (isEditing) {
      return (
        <InputText
          type="number"
          autoFocus
          className={styles.inlineEditInput}
          value={draft}
          disabled={updateReport.isPending}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (cancelledRef.current) {
              cancelledRef.current = false;
              setEditing(null);
              return;
            }
            void commitEdit(row, field);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              cancelledRef.current = true;
              e.currentTarget.blur();
            }
          }}
        />
      );
    }

    if (!isEditable) return <>{display}</>;
    return (
      <div
        className={styles.editableCell}
        title="Double-click to edit"
        onDoubleClick={() => beginEdit(row, field)}
      >
        {display}
      </div>
    );
  };

  return (
    <div className={styles.verticalContainer}>
      <DataTable
        value={lossRuns}
        sortField="policy_year"
        sortOrder={SortOrder.DESC}
      >
        <Column
          field="policy_year"
          sortable
          header="Year"
          body={(data: LossRunByYear) => (
            <FlagWrapper
              flags={flagsForLossYear(data, lossRunInfo, "policy_year")}
            >
              <>
                {data.policy_year === "all_years"
                  ? "All Years"
                  : data.policy_year}
              </>
            </FlagWrapper>
          )}
        />
        <Column
          field="description_of_losses"
          header="Description"
          style={{ maxWidth: "300px" }}
        />
        <Column
          header="Total Claims"
          body={(data: LossRunByYear) => data.open_claims + data.closed_claims}
        />
        <Column
          header="Closed Claims"
          body={(data: LossRunByYear) =>
            renderEditableCell(data, "closed_claims", data.closed_claims)
          }
        />
        <Column
          header="Open Claims"
          body={(data: LossRunByYear) =>
            renderEditableCell(data, "open_claims", data.open_claims)
          }
        />
        <Column
          field="ground_up_loss"
          header="Ground Up Loss"
          body={(data: LossRunByYear) =>
            renderEditableCell(
              data,
              "ground_up_loss",
              <FlagWrapper
                flags={flagsForLossYear(data, lossRunInfo, "ground_up_loss")}
              >
                <TerribleMoney
                  dollars={data.ground_up_loss}
                  ifUndefined="N/A"
                />
              </FlagWrapper>
            )
          }
        />
        <Column
          field="net_incurred"
          header="Net Incurred"
          body={(data: LossRunByYear) =>
            renderEditableCell(
              data,
              "net_incurred",
              <FlagWrapper
                flags={flagsForLossYear(data, lossRunInfo, "net_incurred")}
              >
                <TerribleMoney dollars={data.net_incurred} ifUndefined="N/A" />
              </FlagWrapper>
            )
          }
        />
        <Column
          header="Comments"
          align="center"
          body={(data: LossRunByYear) =>
            // Only real policy years (not the "all_years" aggregate or
            // unparseable rows) get a stable key to anchor comments to.
            parsePolicyPeriod(data.policy_year) ? (
              <CommentContainer
                reportId={reportId}
                reportName={reportName}
                scope={{ kind: "loss_year", policyYear: data.policy_year }}
              />
            ) : null
          }
        />
      </DataTable>
    </div>
  );
};

const ClaimByClaimLossRunTable: React.FC<LossRunTableProps> = ({ claims }) => {
  return (
    <div className={styles.verticalContainer}>
      {claims.length === 0 ? (
        <span>No individual claims found</span>
      ) : (
        <DataTable
          value={claims}
          sortField="policy_year"
          sortOrder={SortOrder.DESC}
        >
          <Column field="policy_year" sortable header="Year" />
          <Column
            field="description"
            header="Description"
            style={{ maxWidth: "300px" }}
          />
          <Column
            field="ground_up_loss"
            header="Ground Up Loss"
            body={(data: Loss) => (
              <FlagWrapper flags={flagsForLoss(data, "ground_up_loss")}>
                <TerribleMoney
                  dollars={data.ground_up_loss}
                  ifUndefined="N/A"
                />
              </FlagWrapper>
            )}
          />
          <Column
            field="deductible"
            header="Deductible"
            body={(data: Loss) => (
              <FlagWrapper flags={flagsForLoss(data, "deductible")}>
                <TerribleMoney dollars={data.deductible} ifUndefined="N/A" />
              </FlagWrapper>
            )}
          />
          <Column
            field="net_incurred"
            header="Net Incurred"
            body={(data: Loss) => (
              <FlagWrapper flags={flagsForLoss(data, "net_incurred")}>
                <TerribleMoney dollars={data.net_incurred} ifUndefined="N/A" />
              </FlagWrapper>
            )}
          />
          <Column
            field="is_cat_event"
            header="Is CAT Event"
            body={(data: Loss) =>
              data.is_cat_event ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <span>Yes</span>
                  <CATCloudIcon />
                </div>
              ) : (
                <span>No</span>
              )
            }
          />
        </DataTable>
      )}
    </div>
  );
};
